
import React, { useState, useEffect } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { refreshSession } from '@/services/authService';
import { Profile } from '@/types/auth';

export function useAuthState() {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch user profile from Supabase with proper authentication
  const fetchUserProfile = async (userId: string, userSession?: Session) => {
    try {
      console.log('Fetching user profile for:', userId);
      
      // Use the current session or the passed session for authentication
      const currentSession = userSession || session;
      if (!currentSession) {
        console.log('No session available for profile fetch');
        return;
      }

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error fetching user profile:', error.message);
        return;
      }

      console.log('Profile fetched successfully:', data);
      setProfile(data as Profile);
    } catch (error) {
      console.error('Unexpected error fetching profile:', error);
    }
  };

  useEffect(() => {
    // Set up auth state listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        console.log('Auth state changed:', event, newSession ? 'User authenticated' : 'User not authenticated');
        setSession(newSession);
        setUser(newSession?.user ?? null);
        
        if (newSession?.user) {
          // Fetch profile immediately with the new session
          fetchUserProfile(newSession.user.id, newSession);
        } else {
          setProfile(null);
        }
      }
    );
    
    // Check for existing session
    supabase.auth.getSession().then(async ({ data: { session: initialSession } }) => {
      console.log('Initial session check:', initialSession ? 'Session exists' : 'No session');
      
      if (initialSession?.user) {
        // Try to refresh the session using our custom method
        if (initialSession.expires_at && (initialSession.expires_at * 1000 < Date.now() + 60000)) {
          console.log('Session is about to expire, refreshing...');
          const { data, error } = await refreshSession();
          if (!error && data?.session) {
            const refreshedSession = data.session as Session;
            setSession(refreshedSession);
            setUser(refreshedSession.user);
            await fetchUserProfile(refreshedSession.user.id, refreshedSession);
          } else {
            console.error('Failed to refresh session:', error);
            setSession(initialSession);
            setUser(initialSession.user);
            await fetchUserProfile(initialSession.user.id, initialSession);
          }
        } else {
          setSession(initialSession);
          setUser(initialSession.user);
          await fetchUserProfile(initialSession.user.id, initialSession);
        }
      }
      
      setLoading(false);
    }).catch(error => {
      console.error('Error checking session:', error);
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Set up periodic token refresh
  useEffect(() => {
    if (!session) return;
    
    const checkAndRefreshToken = async () => {
      if (session.expires_at && (session.expires_at * 1000 < Date.now() + 300000)) { // 5 minutes before expiry
        console.log('Proactively refreshing token...');
        const { data, error } = await refreshSession();
        if (error) {
          console.error('Failed to refresh token:', error);
          // Handle failed refresh (possibly log out user if needed)
        }
      }
    };
    
    // Check every 5 minutes
    const interval = setInterval(checkAndRefreshToken, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [session]);

  return {
    session,
    user,
    profile,
    loading,
    fetchUserProfile
  };
}
